import styled from 'styled-components';

// Site-standard wide content container (WideContentContainer semantics):
// width: 100% is REQUIRED — this component is a flex item of the
// app-level ContentContainer (align-items: center), so without an
// explicit width it shrinks to fit-content (~267px) and the unitat
// cards collapse to a single column. max-width = the site's grid-page
// standard (1200px), not the old bespoke 1000px.
export const Container = styled.div`
	width: 100%;
	max-width: 1200px;
	margin: 0 auto;
	padding: 1rem;
`;

// Inputs inside the row share the width evenly and align on a common
// baseline; buttons keep their natural width.
export const Row = styled.div`
	display: flex;
	align-items: flex-end;
	gap: 1rem;
	flex-wrap: wrap;
	margin-bottom: 1rem;
	width: 100%;

	& > div {
		flex: 1 1 200px;
	}
	& > button {
		flex: 0 0 auto;
	}
`;

export const Title = styled.h1`
	font-size: 1.8rem;
	margin: 0.5rem 0;
`;

export const SubTitle = styled.h2`
	font-size: 1.3rem;
	margin: 1rem 0 0.5rem 0;
`;

export const YearSelect = styled.select`
	padding: 0.5rem 0.75rem;
	border: 1px solid #ccc;
	border-radius: 4px;
	font-size: 1rem;
	background: white;
`;

export const SummaryGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
	gap: 0.75rem;
	margin-bottom: 1.5rem;
	width: 100%;
`;

export const SummaryCard = styled.button<{
	$selected?: boolean;
}>`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	padding: 1rem;
	background: ${(p) => (p.$selected ? '#eef4ff' : '#f9f9f9')};
	border: 2px solid ${(p) => (p.$selected ? '#007bff' : 'transparent')};
	border-radius: 8px;
	cursor: pointer;
	text-align: left;
	font-size: 1rem;

	&:hover {
		background: #eef4ff;
	}
`;

export const CardNom = styled.div`
	font-weight: 600;
	color: #333;
`;

export const CardBalance = styled.div<{ $negative?: boolean }>`
	font-size: 1.35rem;
	font-weight: 700;
	color: ${(p) => (p.$negative ? '#c0392b' : '#1e8449')};
`;

export const CardOwed = styled.div`
	font-size: 0.85rem;
	color: #666;
`;

export const BalancePanel = styled.div`
	display: flex;
	gap: 1rem;
	align-items: baseline;
	flex-wrap: wrap;
	padding: 1rem;
	background: #f9f9f9;
	border-radius: 8px;
	margin-bottom: 1rem;
`;

export const BigBalance = styled.span<{ $negative?: boolean }>`
	font-size: 2rem;
	font-weight: 800;
	color: ${(p) => (p.$negative ? '#c0392b' : '#1e8449')};
`;

export const OwedList = styled.ul`
	list-style: none;
	padding: 0;
	margin: 0;
	font-size: 0.95rem;
`;

export const OwedItem = styled.li`
	display: flex;
	justify-content: space-between;
	gap: 1rem;
	padding: 0.15rem 0;
`;

export const MonthHeader = styled.div`
	font-weight: 700;
	font-size: 0.95rem;
	color: #444;
	margin: 1rem 0 0.35rem 0;
	text-transform: capitalize;
	border-bottom: 2px solid #e8e8e8;
	padding-bottom: 0.15rem;

	&:first-child {
		margin-top: 0;
	}
`;

export const SmallSaldo = styled.span`
	display: block;
	font-size: 0.7rem;
	font-weight: 500;
	color: #999;
`;

export const GlobalBanner = styled.div<{ $alert?: boolean }>`
	display: flex;
	gap: 0.6rem;
	align-items: baseline;
	padding: 0.7rem 1rem;
	background: #fffdf2;
	border: 1px solid #f0e6b6;
	border-radius: 8px;
	margin-bottom: 1rem;
	font-size: 0.95rem;
	color: #555;

	${({ $alert }) =>
		$alert &&
		`
		background: #fdf3f2;
		border-color: #f5c9c4;
		color: #8a2c24;
		flex-wrap: wrap;
		`}
`;

/* Mensualitats overview: unitat × 8-month window grid (money roles). */
export const MensualScroll = styled.div`
	overflow-x: auto;
	margin-bottom: 0.5rem;
`;

export const MensualTable = styled.div`
	display: grid;
	grid-template-columns: minmax(9.5rem, max-content) repeat(
		8,
		minmax(58px, 1fr)
	);
	gap: 6px;
	align-items: stretch;
	min-width: 720px;
`;

export const TableHead = styled.div`
	font-size: 0.72rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.4px;
	color: #8a94a0;
	align-self: end;
	padding-bottom: 2px;
`;

export const RowLabel = styled.div`
	font-weight: 700;
	font-size: 0.85rem;
	color: #2d3436;
	display: flex;
	align-items: center;
`;

export const MissingLink = styled.button`
	font: inherit;
	font-size: 0.82rem;
	background: none;
	border: none;
	border-radius: 6px;
	cursor: pointer;
	text-decoration: underline;
	color: inherit;
	padding: 0;

	&:hover {
		background: rgba(0, 0, 0, 0.06);
	}
`;

export const StartCell = styled.button`
	grid-column: span 8;
	font: inherit;
	font-size: 0.82rem;
	font-weight: 600;
	text-align: left;
	padding: 8px 10px;
	border-radius: 8px;
	border: 1px dashed #b2bec3;
	background: #fafbfc;
	color: #636e72;
	cursor: pointer;

	&:hover {
		border-color: #27ae60;
		color: #1e8449;
		background: #e8f6ef;
	}
`;

export const QuickRow = styled.div`
	grid-column: 1 / -1;
	display: flex;
	gap: 10px;
	align-items: center;
	flex-wrap: wrap;
	background: #fff5f4;
	border: 1px solid #e74c3c;
	border-radius: 10px;
	padding: 10px 12px;
	font-size: 0.85rem;
	color: #8a2c24;

	strong {
		color: #c0392b;
	}

	button.close {
		font: inherit;
		background: none;
		border: none;
		font-size: 1.1rem;
		cursor: pointer;
		color: #8a94a0;
		padding: 0 2px;

		&:hover {
			color: #2d3436;
		}
	}
`;

export const TopRow = styled.div`
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
	align-items: center;
	margin-bottom: 1rem;
	width: 100%;
`;

export const TopBadge = styled.span<{ $alert?: boolean }>`
	font-size: 0.85rem;
	border-radius: 999px;
	padding: 5px 14px;
	font-weight: 600;
	background: #fdfcf5;
	border: 1px solid #f0e6b6;
	color: #6b6440;

	${({ $alert }) =>
		$alert &&
		`
		background: #fdecea;
		border-color: #e74c3c;
		color: #c0392b;
		`}
`;

export const DrawerTitle = styled.h3`
	font-size: 1rem;
	display: flex;
	align-items: baseline;
	gap: 8px;
	margin: 0;
`;

export const MensualCell = styled.button<{
	$state: 'paid' | 'skipped' | 'missing' | 'future';
	$isCurrent?: boolean;
}>`
	font: inherit;
	min-height: 52px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 2px;
	padding: 4px 6px;
	border-radius: 8px;
	text-align: center;
	cursor: default;

	${({ $state }) =>
		$state === 'skipped' &&
		`
		background: #f4f6f7;
		border: 1px solid #b2bec3;
		color: #636e72;
		`}

	span {
		font-size: 0.66rem;
		font-weight: 600;
		text-transform: capitalize;
		opacity: 0.75;
	}
	strong {
		font-size: 0.78rem;
	}

	${({ $state }) =>
		$state === 'paid' &&
		`
		background: #e8f6ef;
		border: 1px solid #27ae60;
		color: #1e8449;
		`}

	${({ $state }) =>
		$state === 'future' &&
		`
		background: #fafbfc;
		border: 1px solid #eceff1;
		color: #aab2b8;
		`}

	${({ $state }) =>
		$state === 'missing' &&
		`
		background: #fdecea;
		border: 1px solid #e74c3c;
		color: #c0392b;
		cursor: pointer;

		&:hover {
			background: #fbdcd9;
		}
		`}

	${({ $isCurrent }) =>
		$isCurrent &&
		`
		outline: 2px solid #0984e3;
		outline-offset: -2px;
		`}
`;

export const EntriesList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	margin-bottom: 1.5rem;
`;

export const EntryRow = styled.div<{
	$kind: 'mensual' | 'despesa' | 'ajust';
}>`
	display: grid;
	grid-template-columns: 8rem 1fr auto auto;
	grid-template-areas: 'date main amount actions';
	align-items: center;
	gap: 0.75rem;
	padding: 0.6rem 0.9rem;
	border-radius: 6px;
	background: ${(p) =>
		p.$kind === 'despesa'
			? '#fdf3f2'
			: p.$kind === 'mensual'
				? '#f2faef'
				: '#f4f4f4'};
	border: 1px solid
		${(p) =>
			p.$kind === 'despesa'
				? '#f5c6c2'
				: p.$kind === 'mensual'
					? '#cfe8c5'
					: '#e0e0e0'};

	@media (max-width: 700px) {
		grid-template-columns: 1fr auto;
		grid-template-areas:
			'date amount'
			'main main'
			'actions actions';
	}
`;

export const EntryDate = styled.div`
	grid-area: date;
	font-size: 0.85rem;
	color: #555;
	white-space: pre-line;
`;

export const EntryMain = styled.div`
	grid-area: main;
	min-width: 0;
`;

export const EntryConcept = styled.div`
	font-weight: 600;
	color: #222;
	overflow-wrap: anywhere;
`;

export const EntryMeta = styled.div`
	font-size: 0.8rem;
	color: #777;
`;

export const EntryAmount = styled.div<{ $negative?: boolean }>`
	grid-area: amount;
	font-weight: 700;
	text-align: right;
	font-size: 1.05rem;
	color: ${(p) => (p.$negative ? '#c0392b' : '#1e8449')};
	white-space: nowrap;
`;

export const EntryActions = styled.div`
	grid-area: actions;
	display: flex;
	gap: 0.4rem;
	flex-wrap: wrap;
`;

export const SmallButton = styled.button<{ $danger?: boolean }>`
	padding: 0.3rem 0.7rem;
	font-size: 0.8rem;
	border: 1px solid #ccc;
	background: white;
	border-radius: 4px;
	cursor: pointer;
	color: ${(p) => (p.$danger ? '#c0392b' : '#333')};

	&:hover:not(:disabled) {
		border-color: #007bff;
		color: #007bff;
	}

	&:disabled {
		color: #999;
		cursor: not-allowed;
	}
`;

export const ReceiptLink = styled.a`
	font-size: 0.8rem;
	color: #007bff;
	text-decoration: underline;
`;

export const Panel = styled.div`
	background: #f9f9f9;
	border-radius: 8px;
	padding: 1rem 1.25rem;
	margin-bottom: 1.5rem;
`;

export const Badge = styled.span<{ $tone?: 'green' | 'red' | 'gray' }>`
	display: inline-block;
	padding: 0.1rem 0.5rem;
	border-radius: 999px;
	font-size: 0.75rem;
	font-weight: 600;
	background: ${(p) =>
		p.$tone === 'green'
			? '#c8e6c9'
			: p.$tone === 'red'
				? '#f5c6c2'
				: '#e0e0e0'};
	color: ${(p) =>
		p.$tone === 'green' ? '#1b5e20' : p.$tone === 'red' ? '#8e2e26' : '#555'};
	margin-left: 0.4rem;
`;

export const Suggestion = styled.div`
	font-size: 0.8rem;
	color: #666;
	margin-top: 0.2rem;

	& > button {
		font: inherit;
		font-size: inherit;
		color: inherit;
		background: none;
		border: none;
		padding: 0;
		cursor: pointer;
		text-decoration: underline;

		&:hover {
			color: #2d3436;
		}
	}
`;

export const NoteText = styled.div`
	font-size: 0.8rem;
	color: #777;
	font-style: italic;
`;

export const NameDropdown = styled.ul`
	position: absolute;
	top: calc(100% + 2px);
	left: 0;
	right: 0;
	background: white;
	border: 1px solid #ccc;
	border-radius: 4px;
	list-style: none;
	margin: 0;
	padding: 0;
	z-index: 30;
	max-height: 180px;
	overflow-y: auto;
	box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
`;

export const NameOption = styled.li<{ $active?: boolean }>`
	padding: 0.5rem 0.75rem;
	cursor: pointer;
	background: ${(p) => (p.$active ? '#eef4ff' : 'white')};
	&:hover {
		background: #eef4ff;
	}
`;

export const NameWrapper = styled.div`
	position: relative;
`;

export const AmountColorNote = styled.span<{ $negative?: boolean }>`
	color: ${(p) => (p.$negative ? '#c0392b' : '#1e8449')};
	font-size: 0.8rem;
`;export const MonthGrid = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-bottom: 10px;
`;

export const MonthCell = styled.span<{
	$received?: boolean;
	$dupe?: boolean;
}>`
	padding: 6px 10px;
	border-radius: 6px;
	font-size: 0.78rem;
	font-weight: 600;
	border: 1px solid #dfe6e9;
	background: #f4f6f7;
	color: #aab2b8;

	${({ $received }) =>
		$received &&
		`
		background: #e8f6ef;
		border-color: #27ae60;
		color: #1e8449;`}

	${({ $dupe }) =>
		$dupe &&
		`
		background: #fdecea;
		border-color: #e74c3c;
		color: #c0392b;`}
`;

/* Form row without a trailing button: top-aligned so labels and
   inputs sit on a shared line even when one group carries help text */
export const FormRow = styled(Row)`
	align-items: flex-start;

	& > div > button {
		margin-top: 0;
	}
`;
